import {
  CardContent,
  CardHeader,
  Grid,
  Link,
  List,
  ListItem,
  Typography,
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import PersonIcon from '@material-ui/icons/Person';
import SentimentDissatisfiedIcon from '@material-ui/icons/SentimentDissatisfied';
import Skeleton from '@material-ui/lab/Skeleton';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Card from '../../../../components/Card';
import CardMessage from '../../../../components/CardMessage/CardMessage';
import ImagePreviewDialog, {
  ImageData,
} from '../../../../components/ImagePreviewDialog';
import SelectPeriod from '../../../../components/SelectPeriod';
import { useFeedback } from '../../../../hooks/feedback';
import { EmployeePunchCardDTO } from '../../../../models/dto/EmployeePunchCardDTO';
import { LogDistanceDTO } from '../../../../models/dto/LogDistanceDTO';
import { PeriodDTO } from '../../../../models/dto/PeriodDTO';
import { getLogDistanceImageData } from '../../../../services/peopleManagement.service';
import { AppState } from '../../../../store';
import { getEmployeePunchCard } from '../../../../store/actions/employeesActions';
import { EmployeesReducerData } from '../../../../store/reducers/employeesReducer';
import { isNullOrEmpty } from '../../../../utils/validation.util';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      height: '100%',
    },
    cardContentContainer: {
      height: 'calc(100% - 104px)',
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingTop: 0,
      paddingLeft: 0,
      paddingRight: 0,
    },
    skeletonContainer: {
      padding: '2px 16px',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
  }),
);

interface PhotoHistoryListItemSkeletonProps {
  amount?: number;
}

const PhotoHistoryListItemSkeleton: React.FC<PhotoHistoryListItemSkeletonProps> = ({
  amount = 1,
}) => {
  const classes = useStyles();

  return (
    <>
      {Array.apply(0, Array(amount)).map(function (_, index) {
        return (
          <Grid
            key={index}
            container
            spacing={1}
            alignItems="center"
            className={classes.skeletonContainer}
          >
            <Grid item xs={3}>
              <Skeleton animation="wave" height={25} width="100%" />
            </Grid>
            <Grid item xs={5}>
              <Skeleton animation="wave" height={25} width="100%" />
            </Grid>
            <Grid item xs={4}>
              <Skeleton animation="wave" height={25} width="100%" />
            </Grid>
          </Grid>
        );
      })}
    </>
  );
};

const PhotoHistoryCard: React.FC = () => {
  const classes = useStyles();
  const [periodSelected, setPeriodSelected] = useState<PeriodDTO | null>(null);
  const [loginData, setLoginData] = useState<LogDistanceDTO[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingImage, setLoadingImage] = useState<boolean>(false);
  const [openImagePreviewDialog, setOpenImagePreviewDialog] = useState<boolean>(
    false,
  );
  const [imagePreviewDialogTitle, setImagePreviewDialogTitle] = useState('');
  const [fileHashSelected, setFileHashSelected] = useState<string>('');
  const [logDistanceImageSelected, setLogDistanceImageSelected] = useState<
    ImageData | undefined
  >();
  const { addFeedback } = useFeedback();

  const getLogDistanceImage = async (fileHash: string) => {
    setLoadingImage(true);
    try {
      const data = await getLogDistanceImageData(fileHash);

      setLogDistanceImageSelected({
        contentType: data.contentType,
        fileBuffer: data.fileBuffer,
      });
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingImage(false);
    }
  };

  const handleOpenImagePreviewDialog = (
    fileHash: string,
    employeeName: string,
    date: string,
  ) => {
    setOpenImagePreviewDialog(true);
    setImagePreviewDialogTitle(`${date} - ${employeeName}`);
    getLogDistanceImage(fileHash);
    setFileHashSelected(fileHashSelected);
  };

  const { employeeSelected }: EmployeesReducerData = useSelector(
    (state: AppState) => state.employeesReducer,
  );

  useEffect(() => {
    async function getPunchCard() {
      try {
        if (employeeSelected && periodSelected) {
          setLoading(true);
          const punchCard = await getEmployeePunchCard(
            employeeSelected,
            periodSelected,
          );

          const data: EmployeePunchCardDTO[] = punchCard
            .filter(register => !isNullOrEmpty(register.logDistancia))
            .reverse();

          const loginLog: LogDistanceDTO[] = [];
          data.forEach(register => loginLog.push(...register.logDistancia));

          setLoginData(loginLog);
        }
      } catch (error) {
        addFeedback({
          type: 'error',
          description:
            'Infelizmente, encontramos uma falha ao buscar pelas fotos utilizadas.',
        });
      } finally {
        setLoading(false);
      }
    }

    getPunchCard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeSelected, periodSelected]);

  return (
    <>
      <Card className={classes.card}>
        <CardHeader
          style={{ paddingBottom: 8 }}
          title={
            <Typography color="textSecondary" variant="h5">
              Últimas Fotos Utilizadas
            </Typography>
          }
        />
        <SelectPeriod
          disableUnderline
          value={periodSelected}
          handlePeriodChange={setPeriodSelected}
        />
        <CardContent className={classes.cardContentContainer}>
          {loading ? (
            <PhotoHistoryListItemSkeleton amount={8} />
          ) : (
            <>
              {employeeSelected ? (
                <>
                  {loginData.length > 0 ? (
                    <List>
                      {loginData.map((log, index) => (
                        <ListItem key={index} divider>
                          <Typography
                            style={{
                              display: 'inline',
                              marginRight: 16,
                            }}
                          >
                            {log.criadoEm}
                          </Typography>
                          <Link
                            component="button"
                            onClick={() =>
                              handleOpenImagePreviewDialog(
                                log.loginHash,
                                employeeSelected.nome,
                                log.criadoEm || '',
                              )
                            }
                            color="primary"
                          >
                            Visualizar Foto
                          </Link>
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <CardMessage
                      icon={
                        <SentimentDissatisfiedIcon
                          style={{ fontSize: 100, opacity: 0.4 }}
                        />
                      }
                      message="Nenhum resultado"
                    />
                  )}
                </>
              ) : (
                <CardMessage
                  icon={<PersonIcon style={{ fontSize: 100, opacity: 0.4 }} />}
                  message="Selecione um funcionário"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      {openImagePreviewDialog && (
        <ImagePreviewDialog
          open={openImagePreviewDialog}
          title={imagePreviewDialogTitle}
          loading={loadingImage}
          image={logDistanceImageSelected}
          handleClose={() => setOpenImagePreviewDialog(false)}
        />
      )}
    </>
  );
};

export default PhotoHistoryCard;
